/**
 * The company's three Team tables, as the Directory is: the company's Teams on
 * its dashboard, one Team's members on that Team's dashboard, and one member's
 * Teams on theirs. Postgres (through api2) picks and orders each page; every
 * row then binds its own Firestore document and stays live (LiveDoc). Money
 * (tokens used, balances, budgets) never comes from Postgres: those cells read
 * the live documents, and the CSV, which is built from the Postgres rows alone,
 * leaves them out.
 *
 * This module is the pure part of the three: their columns, the sorts the
 * endpoints can make, their Filters boxes, the requests, the stand-in each row
 * shows until its document arrives, and the CSV. One vocabulary across the
 * three (and the Directory): a membership is Joined or Pending; a Team is Active
 * or Deactivated.
 */

import moment from 'moment-timezone';
import { CompanyCustomFieldDef, DbTimestamp, Team, TeamMember } from '@run-diffusion/shared';
import {
	ListOrder,
	TeamListRow,
	TeamMemberListRow,
	TeamRoleRef,
	TeamMembersRequest,
	TeamMembersSort,
	TeamsListRequest,
	TeamsSort,
} from '@/utils/teamsApi';
import { PanelFilters } from '@/utils/panelFilters';
import { ListViewOptions } from '@/utils/listView';

export const TEAM_TABLES_ITEMS_PER_PAGE_OPTIONS: number[] = [25, 50, 100];
export const TEAM_TABLES_DEFAULT_ITEMS_PER_PAGE: number = TEAM_TABLES_ITEMS_PER_PAGE_OPTIONS[0];

// A custom field's column is `customFields.<key>`, as the Firestore-era tables named it.
export const TEAM_CUSTOM_FIELD_HEADER_PREFIX = 'customFields.';

export const MEMBERSHIP_STATUS = {
	JOINED: 'joined',
	PENDING: 'pending',
} as const;

export const TEAM_ACTIVATION = {
	ACTIVE: 'active',
	DEACTIVATED: 'deactivated',
} as const;

/** Joined or Pending: the Status field on every table that lists memberships. */
export const MEMBERSHIP_STATUS_ITEMS: { text: string; value: string }[] = [
	{ text: 'Joined', value: MEMBERSHIP_STATUS.JOINED },
	{ text: 'Pending', value: MEMBERSHIP_STATUS.PENDING },
];

/** Active or Deactivated: always one or the other, never cleared. */
export const TEAM_ACTIVATION_ITEMS: { text: string; value: string }[] = [
	{ text: 'Active', value: TEAM_ACTIVATION.ACTIVE },
	{ text: 'Deactivated', value: TEAM_ACTIVATION.DEACTIVATED },
];

function sortDirection(options: Partial<ListViewOptions> | null | undefined): ListOrder {
	return options && Array.isArray(options.sortDesc) && options.sortDesc[0] === true ? 'desc' : 'asc';
}

function firstSortHeader(options: Partial<ListViewOptions> | null | undefined): string | null {
	return options && Array.isArray(options.sortBy) && options.sortBy[0] ? options.sortBy[0] : null;
}

function isoDate(iso: string | null | undefined): string {
	return iso ? moment(iso).format('YYYY-MM-DD') : '';
}

function activationIsActive(activation: string | null | undefined): boolean {
	return activation !== TEAM_ACTIVATION.DEACTIVATED;
}

export interface TableHeaderValue {
	text: string;
	value: string;
}

// ---------------------------------------------------------------------------
// One Team's members (the Team dashboard)
// ---------------------------------------------------------------------------

export const TEAM_MEMBERS_HEADER_VALUE = {
	EMAIL: 'email',
	TEAM_ROLE: 'teamRole',
	JOINED: 'joined',
	ACTIONS: 'actions',
} as const;

export interface TeamMembersPanelFilters extends PanelFilters {
	status: string | null;
	teamRoleId: string | null;
}

export const TEAM_MEMBERS_RESTING_FILTERS: TeamMembersPanelFilters = {
	status: null,
	teamRoleId: null,
};

export const TEAM_MEMBERS_DEFAULT_OPTIONS: ListViewOptions = {
	itemsPerPage: TEAM_TABLES_DEFAULT_ITEMS_PER_PAGE,
	sortBy: [TEAM_MEMBERS_HEADER_VALUE.EMAIL],
	sortDesc: [false],
};

const TEAM_MEMBERS_SORT_BY_HEADER: Record<string, TeamMembersSort> = {
	[TEAM_MEMBERS_HEADER_VALUE.EMAIL]: 'email',
	[TEAM_MEMBERS_HEADER_VALUE.JOINED]: 'joined',
};

export function isSortableTeamMembersHeader(header: string | null | undefined): boolean {
	return !!header && !!TEAM_MEMBERS_SORT_BY_HEADER[header];
}

export interface BuildTeamMembersRequestInput {
	teamId: string | null;
	companyId: string | null;
	accountTeamId: string | null;
	cursor: string | null;
	options: Partial<ListViewOptions>;
	searchQuery: string;
	filters: TeamMembersPanelFilters;
}

export function buildTeamMembersRequest(input: BuildTeamMembersRequestInput): TeamMembersRequest {
	const header: string | null = firstSortHeader(input.options);
	const status: string | null = input.filters.status;
	return {
		teamId: input.teamId || '',
		companyId: input.companyId,
		accountTeamId: input.accountTeamId,
		cursor: input.cursor,
		limit: Number(input.options.itemsPerPage) || TEAM_TABLES_DEFAULT_ITEMS_PER_PAGE,
		sort: (header && TEAM_MEMBERS_SORT_BY_HEADER[header]) || 'email',
		order: sortDirection(input.options),
		q: input.searchQuery || null,
		status: status === MEMBERSHIP_STATUS.JOINED || status === MEMBERSHIP_STATUS.PENDING ? status : null,
		teamRoleId: input.filters.teamRoleId,
	};
}

/**
 * What a member's row shows until its membership document arrives: the same
 * fields, read from the Postgres row, in the document's own shape. The invite
 * link waits for the live document (its code is never in Postgres's answer).
 */
export function stubTeamMemberFromRow(row: TeamMemberListRow, toTimestamp: (iso: string) => DbTimestamp): TeamMember {
	return {
		id: row.key,
		email: row.email,
		userId: row.userId,
		acceptedAt: row.joined ? toTimestamp(row.joined) : null,
		isAccepted: !row.isPending,
		createdAt: row.invited ? toTimestamp(row.invited) : null,
		...(row.isPending && row.teamRole ? { teamUserRoleDefId: row.teamRole.id } : {}),
	} as unknown as TeamMember;
}

export function teamMemberDownloadHeaders(showTeamRole: boolean, teamLabel: string): TableHeaderValue[] {
	return [
		{ text: 'Email', value: 'email' },
		...(showTeamRole ? [{ text: `${teamLabel} Role`, value: 'teamRole' }] : []),
		{ text: 'Status', value: 'status' },
		{ text: 'Joined', value: 'joined' },
		{ text: 'Invited', value: 'invited' },
	];
}

export interface TeamMemberDownloadRow {
	key: string;
	email: string;
	teamRole: string;
	status: string;
	joined: string;
	invited: string;
}

export function teamMemberDownloadRow(row: TeamMemberListRow): TeamMemberDownloadRow {
	return {
		key: row.key,
		email: row.email,
		teamRole: (row.teamRole && row.teamRole.name) || '',
		status: row.isPending ? 'Pending' : 'Joined',
		joined: isoDate(row.joined),
		invited: isoDate(row.invited),
	};
}

// ---------------------------------------------------------------------------
// Rows that are Teams: the company's Teams, and one member's Teams
// ---------------------------------------------------------------------------

export const TEAMS_HEADER_VALUE = {
	NAME: 'name',
	MEMBERS: 'memberCount',
	TEAM_ROLE: 'teamRole',
	JOINED: 'joined',
	ACTIONS: 'actions',
} as const;

const TEAMS_SORT_BY_HEADER: Record<string, TeamsSort> = {
	[TEAMS_HEADER_VALUE.NAME]: 'name',
	[TEAMS_HEADER_VALUE.MEMBERS]: 'member_count',
};

/** A Team table can sort by name, member count, and any custom field the company defines. */
export function isSortableTeamsHeader(header: string | null | undefined, customFieldKeys: string[]): boolean {
	if (!header) return false;
	if (TEAMS_SORT_BY_HEADER[header]) return true;
	return header.startsWith(TEAM_CUSTOM_FIELD_HEADER_PREFIX)
		&& customFieldKeys.includes(header.slice(TEAM_CUSTOM_FIELD_HEADER_PREFIX.length));
}

export const TEAMS_DEFAULT_OPTIONS: ListViewOptions = {
	itemsPerPage: TEAM_TABLES_DEFAULT_ITEMS_PER_PAGE,
	sortBy: [TEAMS_HEADER_VALUE.NAME],
	sortDesc: [false],
};

function teamsSortFor(options: Partial<ListViewOptions>): { sort: TeamsSort; customFieldKey: string | null } {
	const header: string | null = firstSortHeader(options);
	if (header && TEAMS_SORT_BY_HEADER[header]) return { sort: TEAMS_SORT_BY_HEADER[header], customFieldKey: null };
	if (header && header.startsWith(TEAM_CUSTOM_FIELD_HEADER_PREFIX)) {
		return { sort: 'custom_field', customFieldKey: header.slice(TEAM_CUSTOM_FIELD_HEADER_PREFIX.length) };
	}
	return { sort: 'name', customFieldKey: null };
}

/** The company's Teams: the dashboard's Active or Deactivated half, or the Teams some people are on. */
export interface CompanyTeamsPanelFilters extends PanelFilters {
	activation: string;
	// Their addresses, so a person who has only been invited can be picked too
	memberEmails: string[];
}

export const COMPANY_TEAMS_RESTING_FILTERS: CompanyTeamsPanelFilters = {
	activation: TEAM_ACTIVATION.ACTIVE,
	memberEmails: [],
};

export interface BuildCompanyTeamsRequestInput {
	companyId: string | null;
	accountTeamId: string | null;
	cursor: string | null;
	options: Partial<ListViewOptions>;
	searchQuery: string;
	filters: CompanyTeamsPanelFilters;
}

export function buildCompanyTeamsRequest(input: BuildCompanyTeamsRequestInput): TeamsListRequest {
	const { sort, customFieldKey } = teamsSortFor(input.options);
	return {
		companyId: input.companyId || '',
		accountTeamId: input.accountTeamId,
		cursor: input.cursor,
		limit: Number(input.options.itemsPerPage) || TEAM_TABLES_DEFAULT_ITEMS_PER_PAGE,
		sort,
		customFieldKey,
		order: sortDirection(input.options),
		isActive: activationIsActive(input.filters.activation),
		q: input.searchQuery || null,
		memberEmails: input.filters.memberEmails,
	};
}

/** One member's Teams: the ones they have joined, the ones they are invited to, or both; Active or Deactivated. */
export interface MemberTeamsPanelFilters extends PanelFilters {
	status: string | null;
	activation: string;
}

export const MEMBER_TEAMS_RESTING_FILTERS: MemberTeamsPanelFilters = {
	status: null,
	activation: TEAM_ACTIVATION.ACTIVE,
};

export interface BuildMemberTeamsRequestInput {
	companyId: string | null;
	accountTeamId: string | null;
	memberEmail: string | null;
	cursor: string | null;
	options: Partial<ListViewOptions>;
	searchQuery: string;
	filters: MemberTeamsPanelFilters;
}

export function buildMemberTeamsRequest(input: BuildMemberTeamsRequestInput): TeamsListRequest {
	const { sort, customFieldKey } = teamsSortFor(input.options);
	const status: string | null = input.filters.status;
	return {
		companyId: input.companyId || '',
		accountTeamId: input.accountTeamId,
		cursor: input.cursor,
		limit: Number(input.options.itemsPerPage) || TEAM_TABLES_DEFAULT_ITEMS_PER_PAGE,
		sort,
		customFieldKey,
		order: sortDirection(input.options),
		isActive: activationIsActive(input.filters.activation),
		q: input.searchQuery || null,
		memberEmails: input.memberEmail ? [input.memberEmail] : [],
		memberStatus: status === MEMBERSHIP_STATUS.JOINED || status === MEMBERSHIP_STATUS.PENDING ? status : null,
	};
}

/**
 * What a Team's row shows until its document arrives: its name, activation and
 * custom fields from the Postgres row. Budgets and balances are not in it: the
 * money cells wait for the live documents, as they always have.
 */
export function stubTeamFromRow(row: TeamListRow, companyId: string | null, toTimestamp: (iso: string) => DbTimestamp): Team {
	const customFields: Record<string, string> = {};
	(row.customFields || []).forEach((field) => {
		if (field.value !== null) customFields[field.key] = field.value;
	});
	return {
		id: row.id,
		name: row.name,
		companyId,
		isActive: row.isActive,
		customFields,
		createdAt: row.created ? toTimestamp(row.created) : null,
		userIds: {},
	} as unknown as Team;
}

/** A Team's row in the CSV: its own columns and custom fields, from Postgres alone, never money. */
export function teamDownloadHeaders(rows: TeamListRow[], teamLabel: string, extra: TableHeaderValue[]): TableHeaderValue[] {
	const customFieldHeaders: TableHeaderValue[] = [];
	const seen: Record<string, true> = {};
	(rows || []).forEach((row: TeamListRow) => {
		(row.customFields || []).forEach((field) => {
			if (seen[field.key]) return;
			seen[field.key] = true;
			customFieldHeaders.push({ text: field.label || field.key, value: `${TEAM_CUSTOM_FIELD_HEADER_PREFIX}${field.key}` });
		});
	});
	return [
		{ text: teamLabel, value: 'name' },
		...customFieldHeaders,
		...extra,
	];
}

export interface TeamDownloadRow {
	id: string;
	name: string;
	customFields: Record<string, string>;
	memberCount: number;
	pendingCount: number;
	status: string;
	teamRole: string;
	joined: string;
	activation: string;
}

export function teamDownloadRow(row: TeamListRow): TeamDownloadRow {
	const customFields: Record<string, string> = {};
	(row.customFields || []).forEach((field) => {
		customFields[field.key] = field.value || '';
	});
	const membership = row.membership;
	return {
		id: row.id,
		name: row.name,
		customFields,
		memberCount: row.memberCount,
		pendingCount: row.pendingCount,
		status: membership ? (membership.isPending ? 'Pending' : 'Joined') : '',
		teamRole: (membership && membership.teamRole && membership.teamRole.name) || '',
		joined: membership ? isoDate(membership.joined) : '',
		activation: row.isActive ? 'Active' : 'Deactivated',
	};
}

/** The CSV sorts like the table where it can: by name, else as the rows came. */
export function teamTablesDownloadSort(options: Partial<ListViewOptions> | null | undefined, sortableValues: string[]): { sortBy: string[]; sortDesc: boolean[] } {
	const header: string | null = firstSortHeader(options);
	if (header && sortableValues.includes(header)) {
		return { sortBy: [header], sortDesc: [sortDirection(options) === 'desc'] };
	}
	return { sortBy: [], sortDesc: [] };
}

export interface JustAddedTeamInput {
	id: string;
	name: string;
	// The values the Team was created with, by custom field key
	customFields: Record<string, string>;
	customFieldDefs: CompanyCustomFieldDef[];
	createdIso: string;
}

/**
 * A Team just created on this page, as the company's Teams list has its rows,
 * to pin there until the listing has it (utils/justAdded.ts). Its row binds the
 * Team itself, so anything this does not know arrives live.
 */
export function justAddedTeamRow(input: JustAddedTeamInput): TeamListRow {
	return {
		id: input.id,
		name: input.name,
		created: input.createdIso,
		isActive: true,
		customFields: input.customFieldDefs.map((fieldDef: CompanyCustomFieldDef) => ({
			key: fieldDef.key,
			label: fieldDef.label,
			value: input.customFields[fieldDef.key] || null,
		})),
		memberCount: 0,
		pendingCount: 0,
		membership: null,
	};
}

export interface JustAddedTeamMemberInput {
	email: string;
	invitedIso: string;
	// The role the invitation grants, when known
	teamRole: TeamRoleRef | null;
}

/**
 * An address just invited to a Team on this page, as that Team's members list
 * has its rows, to pin there until the listing has it. Its row binds the
 * membership itself, whose id is the address.
 */
export function justAddedTeamMemberRow(input: JustAddedTeamMemberInput): TeamMemberListRow {
	const key: string = input.email.trim().toLowerCase();
	return {
		key,
		email: key,
		userId: null,
		invited: input.invitedIso,
		joined: null,
		isPending: true,
		companyRoleInviteOnly: false,
		teamRole: input.teamRole,
		companyRole: null,
		teams: [],
	};
}
